import { Injectable } from '@angular/core';
import { OrderDetailsPrimer, OrderDetailsReaction, OrderDetailsSample } from '@shared/interfaces';
import { BehaviorSubject, Subject } from 'rxjs';
import { RetestReasonEnum } from '../../../_shared/enums';
import { CkmSetupStepsEnum, FilterOptionsEnum } from '../enums';
import { OrderSampleDetails, OrderShort, PrimerDetails, SampleDetails, SubmitRetestData } from '../interfaces';
import { SubmitRetestPayload } from '../interfaces/ckm-submit-retest-payload.interface';

@Injectable()
export class CkmRetestService {
    private samplesData: OrderSampleDetails | null = null;
    private orderDetails: SubmitRetestData = {} as SubmitRetestData;

    readonly navigateToStepEvent$: Subject<CkmSetupStepsEnum> = new Subject<CkmSetupStepsEnum>();
    readonly validateAndNavigateToStepEvent$ = new Subject<{
        currentStep: CkmSetupStepsEnum;
        newStep: CkmSetupStepsEnum;
    }>();

    readonly retestReasonChanged$: Subject<void> = new Subject<void>();
    readonly expandAllChanged$: Subject<boolean> = new Subject<boolean>();
    readonly sampleExpandChanged$: Subject<void> = new Subject<void>();

    readonly primerSelectChanged$: Subject<void> = new Subject<void>();
    readonly primersVisibilityChanged$: Subject<void> = new Subject<void>();

    readonly displayEmptyStateChanged$: Subject<boolean> = new Subject<boolean>();
    readonly progressLostWarningModal$: Subject<boolean> = new Subject<boolean>();
    readonly isRetestSubmittedSuccessfully$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    step2SearchValue: { type: FilterOptionsEnum; value: string }[] = [];
    orderToStartNewRetest: OrderShort | null = null;

    getSamplesData(): OrderSampleDetails | null {
        return this.samplesData as OrderSampleDetails;
    }

    setSamplesData(samplesDataModified: OrderSampleDetails | null) {
        this.samplesData = samplesDataModified;
    }

    saveOrder(order: OrderShort | null) {
        if (!order) {
            this.orderDetails = {} as SubmitRetestData;
            return;
        }

        this.orderDetails.order = order;
    }

    saveRetestReason(reason: RetestReasonEnum | null) {
        this.orderDetails.retestReason = reason;
        this.retestReasonChanged$.next();
    }

    saveRequesterAndInformedUsers(requesterId: number, informedUserIds: number[]) {
        this.orderDetails.omRequesterId = requesterId;
        this.orderDetails.informedUserIds = informedUserIds;
    }

    saveDataFromDetailsForm(serviceLevelId: number, requesterId: number, informedUserIds: number[]) {
        this.orderDetails.serviceLevelId = serviceLevelId;
        this.orderDetails.informedUserIds = informedUserIds;
        this.orderDetails.omRequesterId = requesterId;
    }

    getOrderDetails(): SubmitRetestData {
        return this.orderDetails;
    }

    resetRetest() {
        this.samplesData = null;
        this.orderDetails = {} as SubmitRetestData;
        this.isRetestSubmittedSuccessfully$.next(false);
        this.navigateToStep(CkmSetupStepsEnum.QUESTIONNAIRE_RETEST);
    }

    navigateToStep(step: CkmSetupStepsEnum): void {
        this.navigateToStepEvent$.next(step);
    }

    triggerExpandAllChange(value: boolean): void {
        this.expandAllChanged$.next(value);
    }

    triggerSampleExpandChange(): void {
        this.sampleExpandChanged$.next();
    }

    triggerReactionSelectChange(): void {
        this.primerSelectChanged$.next();
    }

    composeCreateRetestPayload(): SubmitRetestPayload {
        return {
            retestReason: this.orderDetails.retestReason ?? RetestReasonEnum.NO_RESULTS,
            orderId: this.orderDetails.order.id,
            reactionIds: this.getSelectedReactionsIds(),
            serviceLevelId: this.orderDetails.serviceLevelId,
            omRequesterId: this.orderDetails.omRequesterId,
            informedUserIds: this.orderDetails.informedUserIds,
        };
    }

    onSearch(searchValues: { type: FilterOptionsEnum; value: string }[]) {
        if (!this.samplesData) return;

        this.step2SearchValue = searchValues;
        let displayEmptyState: boolean = true;

        const sampleNames = searchValues
            .filter(item => item.type === FilterOptionsEnum.SAMPLE)
            .map(item => item.value.toUpperCase());
        const primerNames = searchValues
            .filter(item => item.type === FilterOptionsEnum.PRIMER)
            .map(item => item.value.toUpperCase());

        this.samplesData.samples.forEach(sample => {
            const sampleMatch = sampleNames.length === 0 || sampleNames.includes(sample.sampleName.toUpperCase());
            const primerMatch =
                primerNames.length === 0 ||
                sample.primers.some(primer => primerNames.includes(primer.name.toUpperCase()));

            sample.isHidden = !(sampleMatch && primerMatch);
            sample.isExpanded = !sample.isHidden;

            sample.primers.forEach(primer => {
                primer.isHidden = sample.isHidden
                    ? true
                    : primerMatch && primerNames.length != 0
                      ? !primerNames.includes(primer.name.toUpperCase())
                      : false;

                if (!primer.isHidden && !sample.isHidden) {
                    displayEmptyState = false;
                }
            });
        });

        this.triggerReactionSelectChange();
        this.displayEmptyStateChanged$.next(displayEmptyState);
        this.primersVisibilityChanged$.next();
    }

    private getSelectedReactionsIds(): number[] {
        if (!this.samplesData) return [];

        const reactionsIds: number[] = [];
        this.samplesData.samples.forEach((sample: SampleDetails) => {
            sample.primers.forEach((primer: PrimerDetails) => {
                if (primer.isSelected) {
                    reactionsIds.push(primer.reactionId);
                }
            });
        });

        return reactionsIds;
    }

    getNumberOfSelectedReactions(): number {
        if (!this.samplesData) return 0;

        let count = 0;
        this.samplesData.samples?.forEach((sample: SampleDetails) => {
            count += sample.primers.filter((primer: PrimerDetails) => {
                return primer.isSelected;
            }).length;
        });

        return count;
    }

    getOrderDetailsReactions(): OrderDetailsReaction[] {
        if (!this.samplesData) return [];

        return this.samplesData.samples
            .map((sample: SampleDetails) => {
                return {
                    sampleName: sample.sampleName,
                    primers: sample.primers
                        .filter((primer: PrimerDetails) => primer.isSelected)
                        .map((primer: PrimerDetails) => {
                            return { cancelledAt: null, name: primer.name };
                        }),
                };
            })
            .filter(elem => elem.primers.length > 0);
    }

    getAllUsedSamples(): OrderDetailsSample[] {
        if (!this.samplesData) return [];

        const usedSamples = new Map<string, OrderDetailsSample>();
        this.samplesData.samples.forEach(sample => {
            sample.primers.forEach(primer => {
                if (primer.isSelected) {
                    usedSamples.set(sample.sampleName, {
                        sampleName: sample.sampleName,
                        barcode: sample.barcode,
                        size: sample.size,
                        dnaType: sample.dnaType,
                    });
                    return;
                }
            });
        });

        return Array.from(usedSamples).map(sample => sample[1]);
    }

    getAllUsedPrimers(): OrderDetailsPrimer[] {
        if (!this.samplesData) return [];

        const usedPrimers = new Map<string, OrderDetailsPrimer>();
        this.samplesData.samples.forEach(sample => {
            sample.primers.forEach(primer => {
                if (primer.isSelected) {
                    usedPrimers.set(primer.name, {
                        primerSource: primer.type,
                        name: primer.name,
                        sequence: primer.sequence,
                        barcode: primer.barcode,
                    });
                }
            });
        });

        return Array.from(usedPrimers).map(primer => primer[1]);
    }
}
